import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/components/theme/ThemeProvider';
import { Sun, Moon, Monitor, Check, Plus, Car, UserPlus, FilePlus2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveTeamId } from '@/contexts/TeamContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { LanguageSwitcher } from './LanguageSwitcher';
import { TopBarUserEntry } from './TopBarUserEntry';
import Breadcrumb from './Breadcrumb';
import { useResolvedFirstName, useResolvedUserName } from '@/hooks/useResolvedUserName';
import { useBreadcrumb } from '@/hooks/useBreadcrumb';
import { featureFlags } from '@/config/featureFlags';
import { GlobalSearch, GlobalSearchTrigger, useGlobalSearch } from '@/features/search';
import { TopBarMobile } from './TopBarMobile';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { NotepadButton } from '@/components/notepad/NotepadButton';
import { NotepadDrawer } from '@/components/notepad/NotepadDrawer';
import { QuickCalculatorTrigger, QuickCalculatorPopover } from '@/features/calculator';
import { useQuickCalculatorEnabled } from '@/features/settings/useFeatureFlags';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { RoleBadge } from '@/components/authz/RoleBadge';

function ThemeDropdown() {
  const { t } = useTranslation('common');
  const { theme, resolvedTheme, setTheme } = useTheme();

  const icon =
    theme === 'system' ? (
      <Monitor size={18} />
    ) : resolvedTheme === 'dark' ? (
      <Moon size={18} />
    ) : (
      <Sun size={18} />
    );

  const Item = ({
    value,
    label,
    leftIcon,
  }: {
    value: 'light' | 'dark' | 'system';
    label: string;
    leftIcon: React.ReactNode;
  }) => (
    <DropdownMenuItem
      onClick={() => setTheme(value)}
      className="flex items-center justify-between gap-2"
    >
      <span className="inline-flex items-center gap-2">
        {leftIcon}
        {label}
      </span>
      {theme === value && <Check size={16} />}
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('theme.toggle')}
          title={t('theme.toggle')}
          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border hover:bg-muted transition-colors"
        >
          {icon}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="min-w-[180px]">
        <Item value="light" label={t('theme.light')} leftIcon={<Sun size={16} />} />
        <Item value="dark" label={t('theme.dark')} leftIcon={<Moon size={16} />} />
        <Item value="system" label={t('theme.system')} leftIcon={<Monitor size={16} />} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function TopBar({ onMenuClick }: { onMenuClick?: () => void }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation('common');
  const crumbs = useBreadcrumb();
  useGlobalSearch(); // Register ⌘K keyboard shortcut
  const isMobile = useMediaQuery('(max-width: 640px)');
  const teamId = useActiveTeamId();
  const quickCalculatorEnabled = useQuickCalculatorEnabled(teamId);

  // Get name from user profile (source of truth: /users/{uid}/profile/data)
  const firstName = useResolvedFirstName(user?.uid) || null;
  const resolvedName = useResolvedUserName(user?.uid);

  // Store IDs for pre-paint script in index.html
  React.useEffect(() => {
    if (!user?.uid) return; // Skip if user not authenticated
    try {
      localStorage.setItem('lastTeamId', teamId);
      localStorage.setItem('lastUid', user.uid);
    } catch {
      // Ignore localStorage errors
    }
  }, [teamId, user?.uid]);

  // Get page title from breadcrumbs
  const pageTitle = crumbs.length > 0 ? crumbs[crumbs.length - 1].title : 'modcar';

  // Mobile view - TopBarMobile now handles its own user entry
  if (isMobile) {
    return (
      <>
        <TopBarMobile title={pageTitle} onOpenNav={onMenuClick} />
        <GlobalSearch />
        {/* Quick Calculator Popover - also available on mobile */}
        {quickCalculatorEnabled && <QuickCalculatorPopover />}
      </>
    );
  }

  // Desktop view
  return (
    <header className="w-full">
      <div className="h-14 px-4 md:px-6 lg:px-8 flex items-center gap-4">
        {/* Left: Breadcrumb - can compress */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <Breadcrumb items={crumbs} />
        </div>

        {/* Right: Actions - never wrap, always visible */}
        <nav className="ml-auto flex flex-shrink-0 items-center gap-2 overflow-visible">
          {/* Desktop: Show theme and language separately */}
          <div className="hidden sm:flex items-center gap-2">
            <GlobalSearchTrigger />
            {quickCalculatorEnabled && <QuickCalculatorTrigger />}
            <NotepadButton />
            <ThemeDropdown />
            <LanguageSwitcher teamId={teamId} uid={user?.uid} />
          </div>

          {/* Optional Quick Add Menu (feature-flagged, desktop only) */}
          {featureFlags.topbarQuickAdd && (
            <div className="hidden sm:block">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          aria-label={t('accessibility.quickAdd')}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border hover:bg-muted transition-colors"
                        >
                          <Plus size={18} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem
                          onClick={() => navigate('/vehicles?action=add')}
                          className="cursor-pointer"
                        >
                          <Car className="mr-2 h-4 w-4" />
                          {t('quickAdd.vehicle', 'Nieuw voertuig')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate('/customers?action=add')}
                          className="cursor-pointer"
                        >
                          <UserPlus className="mr-2 h-4 w-4" />
                          {t('quickAdd.customer', 'Nieuwe klant')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => navigate('/sales-invoices?action=add')}
                          className="cursor-pointer"
                        >
                          <FilePlus2 className="mr-2 h-4 w-4" />
                          {t('quickAdd.salesInvoice', 'Nieuwe verkoopfactuur')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => navigate('/purchase-invoices?action=add')}
                          className="cursor-pointer"
                        >
                          <FilePlus2 className="mr-2 h-4 w-4" />
                          {t('quickAdd.purchaseInvoice', 'Nieuwe inkoopfactuur')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TooltipTrigger>
                  <TooltipContent>Quick Add</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}

          {/* Role Badge - Shows active team role (visible on all platforms per INV-UI-12) */}
          <RoleBadge />

          {/* User Menu - Unified for mobile and desktop */}
          <TopBarUserEntry
            user={user ?? undefined}
            firstName={firstName}
            displayName={resolvedName.displayName}
          />
        </nav>
      </div>

      {/* Global Search (command palette) */}
      <GlobalSearch />

      {/* Notepad Drawer (global) */}
      <NotepadDrawer />

      {/* Quick Calculator Popover - only render when enabled */}
      {quickCalculatorEnabled && <QuickCalculatorPopover />}
    </header>
  );
}
